import { LoadingModel } from '../../_core/state/loading.model';
import { GetFilter } from '../services/models/filter.model';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { FiltersService } from '../services/filters.service';
import { ClearFilter, GetFilter as GetFilterAction } from './filters.actions';
import { append, iif, patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { finalize } from 'rxjs/operators';

export type FiltersStateModel = LoadingModel & GetFilter & {
  isLoadingSearch: boolean;
  facetSearch: string;
};

const initialState: FiltersStateModel = {
  isLoading: false,
  isLoadingSearch: false,
  facetSearch: '',
  label: '',
  name: '',
  data: [],
  page: 0,
  size: 0,
  total: 0,
}

@State<FiltersStateModel>({
  name: 'filters',
  defaults: initialState
})
@Injectable()
export class FiltersState {
  private readonly filtersService = inject(FiltersService);

  @Selector()
  static isLoading(state: FiltersStateModel) {
    return state.isLoading;
  }

  @Selector()
  static isLoadingSearch(state: FiltersStateModel) {
    return state.isLoadingSearch;
  }

  @Selector()
  static filter(state: FiltersStateModel) {
    return {
      label: state.label,
      name: state.name,
    }
  }

  @Selector()
  static filterValues(state: FiltersStateModel) {
    return state.data;
  }

  @Selector()
  static pagination(state: FiltersStateModel) {
    return {
      page: state.page,
      size: state.size,
      total: state.total,
    };
  }

  @Action(GetFilterAction)
  getFilter(context: StateContext<FiltersStateModel>, { filterName, params }: GetFilterAction) {
    const { facetSearch: previousFacetSearch } = context.getState();
    context.setState(patch({ isLoading: true, isLoadingSearch: params.facetSearch !== previousFacetSearch }));

    return this.filtersService.getFilter(filterName, params).pipe(
      tap((response) => {
        context.setState(patch({
          facetSearch: params.facetSearch,
          label: response.label,
          name: response.name,
          data: iif(params.facetSearch !== previousFacetSearch, response.data, append(response.data)),
          page: response.page,
          size: response.size,
          total: response.total,
        }));
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false, isLoadingSearch: false }));
      }),
    );
  }

  @Action(ClearFilter)
  clearFilter(context: StateContext<FiltersStateModel>) {
    context.setState(initialState);
  }
}
